/**
 * Tutorial videos viewer + admin CRUD.
 * Each role sees a curated list of videos (admin-controlled). Admin has full CRUD.
 * Player: YouTube/Vimeo embeds, or HTML5 <video> for direct MP4 URLs.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Play, Plus, Edit2, Trash2, X, Video } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { API } from "../utils/format";
import { FieldHint } from "../components/ui/FieldHint";
import { InteractiveTourList } from "../components/InteractiveTour";

const ROLE_LABELS = { admin: "Admin", agent: "Agente", warehouse: "Magazzino", customer: "Cliente" };

const EMPTY = {
  title: "",
  description: "",
  video_url: "",
  target_roles: ["admin", "agent", "warehouse", "customer"],
  section: "general",
  sort_order: 100,
  duration: "",
};

/** Returns an embeddable URL for YouTube/Vimeo or null if it's a direct MP4. */
const getEmbedUrl = (url) => {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/(?:.*v=|embed\/|shorts\/|live\/)|youtu\.be\/)([\w-]{11})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  return null;
};

const isDirectVideo = (url) => /\.(mp4|webm|mov|m4v)(\?|$)/i.test(url || "");

const TutorialsPage = ({ currentUser }) => {
  const isAdmin = currentUser?.role === "admin";
  const [tutorials, setTutorials] = useState([]);
  const [playing, setPlaying] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/tutorials`);
      setTutorials(r.data || []);
    } catch (e) {
      toast.error("Errore nel caricamento dei tutorial");
    }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing("new"); setForm(EMPTY); };
  const startEdit = (t) => {
    setEditing(t.id);
    setForm({ ...EMPTY, ...t });
  };
  const cancel = () => { setEditing(null); setForm(EMPTY); };

  const save = async () => {
    if (!form.title || !form.video_url) { toast.error("Titolo e URL video obbligatori"); return; }
    try {
      if (editing === "new") {
        await axios.post(`${API}/tutorials`, form);
        toast.success("Tutorial creato");
      } else {
        await axios.patch(`${API}/tutorials/${editing}`, form);
        toast.success("Tutorial aggiornato");
      }
      cancel();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare definitivamente questo tutorial?")) return;
    try {
      await axios.delete(`${API}/tutorials/${id}`);
      toast.success("Eliminato");
      load();
    } catch (e) {
      toast.error("Errore");
    }
  };

  const toggleRole = (role) => {
    setForm(prev => {
      const tr = prev.target_roles || [];
      return { ...prev, target_roles: tr.includes(role) ? tr.filter(r => r !== role) : [...tr, role] };
    });
  };

  // Group by section
  const grouped = tutorials.reduce((acc, t) => {
    const k = t.section || "general";
    (acc[k] = acc[k] || []).push(t);
    return acc;
  }, {});

  return (
    <div className="space-y-5" data-testid="tutorials-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-stone-900 flex items-center gap-2">
            <Video className="w-6 h-6 text-[#8A1C2A]" /> Guide & Tutorial
          </h1>
          <p className="text-sm text-stone-600 mt-1">
            {isAdmin
              ? "Avvia i tour interattivi guidati (sotto) o carica video aggiuntivi (YouTube, Vimeo, link MP4 diretto)."
              : "Tour interattivi guidati e video tutorial del tuo portale."}
          </p>
        </div>
        {isAdmin && (
          <Button onClick={startNew} className="bg-[#8A1C2A] text-white" data-testid="add-tutorial-btn">
            <Plus className="w-4 h-4 mr-1" /> Nuovo video
          </Button>
        )}
      </div>

      {/* Interactive guided tours — generated by the system, no video needed */}
      <InteractiveTourList role={currentUser?.role || "admin"} />

      {/* Video tutorials uploaded by admin */}
      {(tutorials.length > 0 || isAdmin) && (
        <div className="pt-2 border-t border-stone-200">
          <h2 className="text-base font-bold text-stone-800 mt-3 mb-2">🎬 Video aggiuntivi caricati dall'admin</h2>
        </div>
      )}

      {tutorials.length === 0 ? (
        isAdmin ? (
          <Card className="p-6 text-center bg-stone-50 border-stone-200">
            <Video className="w-10 h-10 text-stone-400 mx-auto mb-2" />
            <p className="text-sm text-stone-600">Nessun video aggiuntivo caricato.</p>
            <p className="text-xs text-stone-500 mt-1">I tour interattivi sopra coprono già tutte le funzioni principali.</p>
          </Card>
        ) : null
      ) : (
        Object.keys(grouped).sort().map(section => (
          <div key={section}>
            <h2 className="text-sm font-bold uppercase text-stone-500 tracking-wide mb-2">{section}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {grouped[section].map(t => (
                <Card key={t.id} className="overflow-hidden bg-white border-stone-200 hover:shadow-md transition-shadow" data-testid={`tutorial-card-${t.id}`}>
                  <button onClick={() => setPlaying(t)} className="relative w-full aspect-video bg-stone-900 flex items-center justify-center group">
                    <Play className="w-12 h-12 text-white opacity-80 group-hover:opacity-100 group-hover:scale-110 transition-transform" />
                    {t.duration && <span className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-0.5 rounded">{t.duration}</span>}
                  </button>
                  <div className="p-3">
                    <h3 className="font-semibold text-stone-900 line-clamp-2">{t.title}</h3>
                    {t.description && <p className="text-xs text-stone-600 mt-1 line-clamp-2">{t.description}</p>}
                    <div className="flex flex-wrap items-center gap-1 mt-2">
                      {(t.target_roles || []).map(r => (
                        <Badge key={r} className="bg-stone-100 text-stone-700 border border-stone-200 text-[10px]">{ROLE_LABELS[r] || r}</Badge>
                      ))}
                    </div>
                    {isAdmin && (
                      <div className="flex gap-1 mt-2 pt-2 border-t border-stone-100">
                        <Button size="sm" variant="outline" onClick={() => startEdit(t)} className="flex-1 text-xs"><Edit2 className="w-3 h-3 mr-1" />Modifica</Button>
                        <Button size="sm" variant="outline" onClick={() => remove(t.id)} className="text-red-700 border-red-300"><Trash2 className="w-3 h-3" /></Button>
                      </div>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          </div>
        ))
      )}

      {/* Player Dialog */}
      <Dialog open={!!playing} onOpenChange={(o) => !o && setPlaying(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{playing?.title}</DialogTitle>
          </DialogHeader>
          {playing && (
            <div className="space-y-2">
              <div className="relative w-full aspect-video bg-black rounded overflow-hidden">
                {(() => {
                  const embed = getEmbedUrl(playing.video_url);
                  if (embed) {
                    return <iframe title={playing.title} src={embed} className="absolute inset-0 w-full h-full" allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen />;
                  }
                  if (isDirectVideo(playing.video_url)) {
                    return <video controls className="absolute inset-0 w-full h-full" src={playing.video_url} />;
                  }
                  return (
                    <div className="absolute inset-0 flex items-center justify-center text-white text-center p-4">
                      <p>URL non riconosciuto. <a className="underline" href={playing.video_url} target="_blank" rel="noreferrer">Apri in nuova scheda</a></p>
                    </div>
                  );
                })()}
              </div>
              {playing.description && <p className="text-sm text-stone-700">{playing.description}</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && cancel()}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing === "new" ? "Nuovo Tutorial" : "Modifica Tutorial"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Titolo</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Es. Come gestire un ordine" data-testid="tutorial-title-input" />
            </div>
            <div>
              <Label className="flex items-center gap-1">URL Video
                <FieldHint>
                  Supportati: <strong>YouTube</strong> (link normale o /shorts), <strong>Vimeo</strong>, oppure URL diretto a file <strong>MP4/WebM/MOV</strong> (es. su Google Drive con link diretto, Loom, ecc.).
                </FieldHint>
              </Label>
              <Input value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} placeholder="https://www.youtube.com/watch?v=..." data-testid="tutorial-url-input" />
            </div>
            <div>
              <Label>Descrizione (opzionale)</Label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 text-sm bg-white border border-stone-300 rounded-md"
                placeholder="Cosa spiega questo video?"
              />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label>Sezione</Label>
                <Input value={form.section} onChange={(e) => setForm({ ...form, section: e.target.value })} placeholder="orders / products / ..." />
              </div>
              <div>
                <Label>Durata</Label>
                <Input value={form.duration || ""} onChange={(e) => setForm({ ...form, duration: e.target.value })} placeholder="3:42" />
              </div>
              <div>
                <Label>Ordine</Label>
                <Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 100 })} />
              </div>
            </div>
            <div>
              <Label className="flex items-center gap-1">Visibile a
                <FieldHint>Seleziona i ruoli che potranno vedere questo video nel proprio portale.</FieldHint>
              </Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {["admin", "agent", "warehouse", "customer"].map(role => (
                  <button
                    key={role}
                    type="button"
                    onClick={() => toggleRole(role)}
                    className={`px-3 py-1 rounded-md border text-xs ${form.target_roles?.includes(role) ? "bg-[#8A1C2A] text-white border-[#8A1C2A]" : "bg-white text-stone-700 border-stone-300"}`}
                    data-testid={`tutorial-role-${role}`}
                  >
                    {ROLE_LABELS[role]}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={cancel}><X className="w-4 h-4 mr-1" />Annulla</Button>
            <Button onClick={save} className="bg-[#8A1C2A] text-white" data-testid="save-tutorial-btn">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TutorialsPage;
